import { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  uploadImage,
  addPackage,
  getPackage,
  updatePackage,
} from "../../../../../services/firebaseService/endPoints/admin/members";
import { getStoreItemsForRegistration } from "../../../../../services/firebaseService/endPoints/admin/store";
import { postUpdateCDNUtil } from "../../../../../helperFunctions/util";
import { verifyStripeAccountId } from "../../../../../utils/errors";
import logger from "../../../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";

export const useMemberPackageForm = (id, fetchData, close) => {
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [state, setState] = useState({
    status: "active",
    season: null,
    name: "",
    desc: "",
    image: undefined,
    price: {
      amount: 0,
      type: "Per member",
      shipping: 0,
    },
    childPrice: {
      amount: 0,
      shipping: 0,
    },
    offerShipping: "",
    shippingNote: "",
    pickupNote: "",
    productChoice: "None",
    products: [],
    askTshirtSize: false,
    tshirtSizes: [],
    showSizes: false,
    storeItems: [],
    includesChildPrice: false,
    packageExpiryDate: null,
    offerAutorenewal: false,
    packageTimeline: {},
    autoRenewalDate: {
      month: "",
      date: "",
    },
    emailMemo: "",
    editChanges: {
      priceChange: false,
      nameChange: false,
      childPriceChange: false,
    },
    saving: false,
    originalPkgSeason: null,
  });

  const updateState = (updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  };

  const handleDateChange = (name, value) => {
    if (!Array.isArray(value)) {
      const parsedValue = dayjs(value);
      if (!parsedValue.isValid()) {
        setState((prevState) => ({ ...prevState, [name]: null }));
        return;
      }
      value = parsedValue;
    }

    setState((prevState) => ({
      ...prevState,
      [name]: value.set("hour", 0).set("minute", 0).set("second", 0),
    }));
  };

  const savePackage = async () => {
    try {
      const { price, childPrice } = state;
      let isFreePackage = false;

      const totalPrice =
        price?.amount +
        price?.shipping +
        childPrice?.amount +
        childPrice?.shipping;

      if (totalPrice === 0) {
        isFreePackage = true;
      }

      if (
        !isFreePackage &&
        !verifyStripeAccountId(groupData?.paymentDetails?.stripeAccountId)
      ) {
        return;
      }

      if (state?.offerAutorenewal && isFreePackage) {
        alert("Auto-renewal is not available for free packages.");
        return;
      }

      setState((prevState) => ({ ...prevState, saving: true }));
      let data = state;
      data = {
        ...data,
        packageExpiryDate: data.packageExpiryDate
          ? dayjs(data.packageExpiryDate).unix()
          : null,
      };

      if (!state.name) {
        alert("Package name is required");
        setState((prevState) => ({ ...prevState, saving: false }));
        return;
      }

      if (!state.packageExpiryDate) {
        alert("Package expiry date is required");
        setState((prevState) => ({ ...prevState, saving: false }));
        return;
      }

      let image = "";
      if (data.image && typeof data.image !== "string") {
        image = await uploadImage(
          data.image,
          `regForm/${groupData?.id}/pack/${Date.now()}`,
        ).then(({ downloadUrl }) => downloadUrl);
        data.image = image;
      } else {
        if (!data.image) {
          alert("Image is required");
          setState((prevState) => ({ ...prevState, saving: false }));
          return;
        }
      }

      if (!state.offerShipping) {
        alert("Please select shipping option.");
        setState((prevState) => ({ ...prevState, saving: false }));
        return;
      }

      let editChanges = JSON.parse(JSON.stringify(data.editChanges));

      data.packageTimeline = {
        ...data.packageTimeline,
        [state.season]: {
          name: state?.name,
          expiryDate: dayjs(state?.packageExpiryDate).unix() || null,
          season: state?.season,
          package: state.name,
          offerAutorenewal: state?.offerAutorenewal,
          price: state?.price,
          childPrice: state?.childPrice,
          products: state?.products,
          includesChildPrice: state?.includesChildPrice,
        },
      };

      delete data.showSizes;
      delete data.storeItems;
      delete data.editChanges;

      let paymentDetails = groupData.paymentDetails || {};
      let stripeId = paymentDetails.stripeAccountId || "";
      let currency = (paymentDetails.currency || "usd").toLowerCase();

      if (id) {
        await updatePackage(
          data,
          id,
          groupData?.id,
          editChanges,
          stripeId,
          currency,
        );
      } else {
        await addPackage(data, groupData?.id, stripeId, currency);
      }
      postUpdateCDNUtil(groupData?.id);
      if (fetchData) {
        fetchData();
      }
      close();
    } catch (error) {
      setState((prevState) => ({ ...prevState, saving: false }));
      logger.error("Save Package Error: ", error);
      alert(error?.message || "Couldn't save the package. Please try again.");
    }
  };

  useEffect(() => {
    let promises = [];
    if (id) {
      let packagePromise = getPackage(groupData?.id, id).then((data) => {
        setState((prevState) => ({
          ...prevState,
          ...data,
          originalPkgSeason: data.season,
          packageExpiryDate: data.packageExpiryDate
            ? dayjs(data.packageExpiryDate * 1000)
            : null,
          childPrice: data.childPrice || {
            amount: 0,
            shipping: 0,
          },
          saving: false,
        }));
      });

      promises.push(packagePromise);
    }

    let productsPromise = getStoreItemsForRegistration(groupData?.id).then(
      (items) => {
        setState((prevState) => ({ ...prevState, storeItems: items }));
      },
    );

    promises.push(productsPromise);

    Promise.all(promises);
  }, [id, groupData?.id]);

  return {
    state,
    updateState,
    handleDateChange,
    savePackage,
  };
};
